import { useEffect, useState, useCallback } from 'react';

interface UseBookKeyboardOptions {
  onPrev?: () => void;
  onNext?: () => void;
  onToggleSidebar?: () => void;
  onToggleToc?: () => void;
  onFocusSearch?: () => void;
}

export function useBookKeyboard({
  onPrev,
  onNext,
  onToggleSidebar,
  onToggleToc,
  onFocusSearch,
}: UseBookKeyboardOptions) {
  const [showHelp, setShowHelp] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't trigger when typing in inputs
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Don't interfere with Cmd/Ctrl shortcuts
    if (e.metaKey || e.ctrlKey) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        onPrev?.();
        break;
      case 'ArrowRight':
        e.preventDefault();
        onNext?.();
        break;
      case '[':
        e.preventDefault();
        onToggleSidebar?.();
        break;
      case ']':
        e.preventDefault();
        onToggleToc?.();
        break;
      case '/':
        e.preventDefault();
        onFocusSearch?.();
        break;
      case 't':
        e.preventDefault();
        onToggleToc?.();
        break;
      case '?':
        e.preventDefault();
        setShowHelp((prev) => !prev);
        break;
    }
  }, [onPrev, onNext, onToggleSidebar, onToggleToc, onFocusSearch]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { showHelp, setShowHelp };
}

export function KeyboardShortcutsHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className="kbd-help-overlay" onClick={onClose}>
      <div className="kbd-help-panel" onClick={(e) => e.stopPropagation()}>
        <h3>Keyboard Shortcuts</h3>
        {[
          ['←', 'Previous page'],
          ['→', 'Next page'],
          ['[', 'Toggle sidebar'],
          ['] or t', 'Toggle table of contents'],
          ['/', 'Focus search'],
          ['Ctrl+F', 'Find in page'],
          ['?', 'Show this help'],
          ['Esc', 'Close dialog'],
        ].map(([key, desc]) => (
          <div key={key} className="kbd-help-row">
            <span>{desc}</span>
            <kbd>{key}</kbd>
          </div>
        ))}
      </div>
    </div>
  );
}
